import React, { createContext, useContext } from "react";
import { cx } from "./util";
import { useDetailsDismiss } from "./useDetailsDismiss";

const ModalContext = createContext<{ onHide?: () => void }>({});

type ModalProps = {
  show?: boolean;
  onHide?: () => void;
  backdrop?: boolean | "static";
} & React.HTMLAttributes<HTMLDivElement>;

function ModalBase({
  show,
  onHide,
  backdrop,
  className,
  children,
  ...rest
}: ModalProps) {
  if (!show) return null;
  const staticBackdrop = backdrop === "static";
  return (
    <ModalContext.Provider value={{ onHide }}>
      <div
        role="dialog"
        className={cx("modal modal-open", className)}
        {...rest}
      >
        <div className="modal-box">{children}</div>
        {backdrop !== false && (
          <div
            className="modal-backdrop"
            onClick={staticBackdrop ? undefined : onHide}
          />
        )}
      </div>
    </ModalContext.Provider>
  );
}

type ModalHeaderProps = {
  closeButton?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

function ModalHeader({
  closeButton,
  className,
  children,
  ...rest
}: ModalHeaderProps) {
  const { onHide } = useContext(ModalContext);
  return (
    <div
      className={cx("flex items-start justify-between gap-4", className)}
      {...rest}
    >
      <div>{children}</div>
      {closeButton && (
        <button
          type="button"
          className="btn btn-sm btn-circle btn-ghost"
          aria-label="Close"
          onClick={onHide}
        >
          ✕
        </button>
      )}
    </div>
  );
}

const ModalTitle = ({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h3 className={cx("font-bold text-lg", className)} {...rest}>
    {children}
  </h3>
);
const ModalBody = ({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cx("py-4", className)} {...rest}>
    {children}
  </div>
);
const ModalFooter = ({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cx("modal-action", className)} {...rest}>
    {children}
  </div>
);

// eslint-disable-next-line react-refresh/only-export-components -- compound component (Object.assign) the rule can't recognize
export const Modal = Object.assign(ModalBase, {
  Header: ModalHeader,
  Title: ModalTitle,
  Body: ModalBody,
  Footer: ModalFooter,
});

// Dropdowns ride native <details>: open/close needs no React state, and
// selecting an item closes the menu by removing the open attribute.
// eslint-disable-next-line react-refresh/only-export-components -- shared DOM helper, also unit-tested directly
export const closeParentDetails = (e: {
  currentTarget: {
    closest?: (
      selector: string,
    ) => { removeAttribute: (name: string) => void } | null;
  };
}) => {
  const details = e.currentTarget.closest && e.currentTarget.closest("details");
  if (details) details.removeAttribute("open");
};

type DropdownButtonProps = {
  id?: string;
  title?: React.ReactNode;
  pullRight?: boolean;
  noCaret?: boolean;
  summaryClassName?: string;
} & Omit<React.DetailsHTMLAttributes<HTMLDetailsElement>, "title">;

export function DropdownButton({
  id,
  title,
  pullRight,
  noCaret,
  className,
  // Twirl parity: dashboard kebabs use btn-ghost btn-sm / btn-ghost btn-xs
  summaryClassName = "btn",
  style,
  children,
  ...rest
}: DropdownButtonProps) {
  const dismissRef = useDetailsDismiss();
  return (
    <details
      ref={dismissRef}
      className={cx("dropdown", pullRight && "dropdown-end", className)}
      id={id}
      style={style}
      {...rest}
    >
      <summary className={summaryClassName}>{title}</summary>
      <ul className="menu dropdown-content bg-base-100 rounded-box z-50 p-2 shadow w-56">
        {children}
      </ul>
    </details>
  );
}

type MenuItemProps = {
  href?: string;
  target?: string;
  eventKey?: unknown;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
} & Omit<React.LiHTMLAttributes<HTMLLIElement>, "onClick">;

export function MenuItem({
  onClick,
  href,
  target,
  eventKey,
  className,
  children,
  ...rest
}: MenuItemProps) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!href) e.preventDefault();
    closeParentDetails(e);
    if (onClick) onClick(e);
  };
  return (
    <li role="presentation" className={className} {...rest}>
      <a
        role="menuitem"
        href={href || "#"}
        target={target}
        onClick={handleClick}
      >
        {children}
      </a>
    </li>
  );
}
